import { alpha, type Theme } from "@mui/material";
import { type SystemStyleObject } from "@mui/system";
import { MOBILE_BREAKPOINT } from "@/styles/responsive";
import AutorenewOutlined from "@mui/icons-material/AutorenewOutlined";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import ScheduleOutlined from "@mui/icons-material/ScheduleOutlined";

/** primary の半透明は2段だけ。hover と選択中の行の地。 */
export const ALPHA_HOVER = 0.06;
/** primary の半透明は2段だけ。小さなバッジとチップの地。 */
export const ALPHA_TINT = 0.12;
/** ステッパーの現在の段の外側の輪。 */
export const ALPHA_STEP_RING = 0.2;

/** カードの内側余白（SP 16px、PC 24px）。 */
export const SECTION_PADDING = { xs: 2, [MOBILE_BREAKPOINT]: 3 };

/** カードと大きな面。白地、border.main の1px、角丸8、影なし。 */
export const sectionSx = (theme: Theme): SystemStyleObject<Theme> => ({
  boxSizing: "border-box",
  backgroundColor: theme.palette.white.main,
  border: `1px solid ${theme.palette.border.main}`,
  borderRadius: "8px",
});

/** 地図の上に重ねる部品・ダイアログ・表の影（2-5 の2種類のうちの1つ）。 */
export const overlayShadow = (theme: Theme): string => {
  const black = theme.palette.common.black;
  return `0 4px 6px -1px ${alpha(black, 0.1)}, 0 2px 4px -2px ${alpha(
    black,
    0.1
  )}`;
};

/** マイページのカードの影（MenuButton と同じ値）。 */
export const cardShadow = (theme: Theme): string =>
  `0 2px 8px -2px ${alpha(theme.palette.common.black, 0.25)}`;

/** 表コンテナ。角丸4・弱い影・overflow hidden。 */
export const tableContainerSx = (theme: Theme): SystemStyleObject<Theme> => ({
  backgroundColor: theme.palette.white.main,
  borderRadius: "4px",
  boxShadow: overlayShadow(theme),
  overflow: "hidden",
});

/** 内側の行（書類・CostItem）と入力欄の角丸。 */
export const INNER_RADIUS = "4px";

/** 丸アイコンバッジ（40px 以下でだけ使う）。primary の薄地にprimaryのアイコン。 */
export const badgeSx = (
  theme: Theme,
  size: number
): SystemStyleObject<Theme> => ({
  width: Math.min(size, 40),
  height: Math.min(size, 40),
  flexShrink: 0,
  borderRadius: "50%",
  backgroundColor: alpha(theme.palette.primary.main, ALPHA_TINT),
  color: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

/**
 * 小ピル（MUI Chip）の色。primary の薄地＋dark 文字（primary 文字では 4.5:1 に届かない場合がある）。
 */
export const tintChipSx = (theme: Theme): SystemStyleObject<Theme> => ({
  fontWeight: 600,
  color: theme.palette.dark.main,
  backgroundColor: alpha(theme.palette.primary.main, ALPHA_TINT),
});

/**
 * 見た目が小さいボタンのタップ領域を44px以上にする（lakers-responsive-ui の方法）。
 * ボタンに className="TapExpanded" も付ける（compact.ts の minHeight 44 の対象から外れる）。
 */
export const tapExpandedSx = {
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    inset: "-7px -4px",
  },
} as const;

/**
 * 見た目が size px 四方の部品のタップ領域を、上下左右に広げて 44px 四方にする。
 * @param size - 見た目の一辺（px）
 */
export const tapSquareSx = (size: number): SystemStyleObject<Theme> => ({
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    inset: `${Math.min(0, (size - 44) / 2)}px`,
  },
});

/** フォーカスの輪（2px の primary.main、外側 2px）。キーボード操作のときだけ出す。 */
export const focusRingSx = (theme: Theme): SystemStyleObject<Theme> => ({
  "&:focus-visible, &.Mui-focusVisible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: "2px",
  },
});

/** 行hover と選択中の行の地。primary.50 は theme に無いため alpha で代替。 */
export const rowHoverBg = (theme: Theme): string =>
  alpha(theme.palette.primary.main, ALPHA_HOVER);

/** 動きを減らす設定のときに transition を止める。 */
export const NO_TRANSITION = {
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
} as const;

/**
 * 文字の大きさの段（方針書 2-4）。24（SP 20）/18/16/14/13/12 の6段だけを使う。
 * PageContainer の SP 縮小（compact）を掛けない画面で、px で固定する。
 */
export const TEXT = {
  pageTitle: {
    fontSize: { xs: 20, [MOBILE_BREAKPOINT]: 24 },
    fontWeight: 700,
    lineHeight: 1.4,
  },
  section: { fontSize: 18, fontWeight: 700, lineHeight: 1.4 },
  cardTitle: { fontSize: 16, fontWeight: 700, lineHeight: 1.5 },
  body: { fontSize: 14, fontWeight: 400, lineHeight: 1.7 },
  meta: { fontSize: 13, fontWeight: 400, lineHeight: 1.6 },
  label: { fontSize: 12, fontWeight: 400, lineHeight: 1.4 },
  amount: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.4,
    fontVariantNumeric: "tabular-nums",
  },
} as const;

/** 料金内訳の1行（左ラベル／右金額）。発注の CostItem と同形。 */
export const costItemSx = (theme: Theme): SystemStyleObject<Theme> => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 2,
  px: 1.5,
  py: 1,
  mb: 1,
  backgroundColor: theme.palette.white.main,
  border: `1px solid ${theme.palette.border.main}`,
  borderRadius: INNER_RADIUS,
  fontVariantNumeric: "tabular-nums",
  "&:last-of-type": { mb: 0 },
});

export interface StatusStyle {
  color: string;
  dot: string;
  Icon: typeof ScheduleOutlined;
}

/**
 * 進捗ステータスごとの文字色・ドット色・アイコン。
 * 未知のステータスは RECEIVED と同じ見た目。
 */
export const getStatusStyle = (theme: Theme, status: string): StatusStyle => {
  const teal = theme.palette.primary.main;
  switch (status) {
    case "IN_PROGRESS":
      return {
        color: theme.palette.dark.main,
        dot: teal,
        Icon: AutorenewOutlined,
      };
    case "DELIVERED":
      return {
        color: theme.palette.success.dark,
        dot: theme.palette.success.main,
        Icon: CheckCircleOutline,
      };
    default:
      return {
        color: theme.palette.text.secondary,
        dot: theme.palette.text.secondary,
        Icon: ScheduleOutlined,
      };
  }
};

/**
 * ResponsiveTable を包むラッパーの sx。PC の表だけに効く
 * （ヘッダーは primary 塗り・白字、コンテナは角丸4、行の高さ56）。SP のカードには影響しない。
 */
export const responsiveTableWrapSx = (
  theme: Theme
): SystemStyleObject<Theme> => ({
  "& .MuiTableContainer-root": {
    borderRadius: "4px",
    overflow: "hidden",
    boxShadow: overlayShadow(theme),
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.white.main,
    fontWeight: 700,
    borderBottom: 0,
  },
  "& .MuiTableBody-root .MuiTableCell-root": {
    height: 56,
    fontVariantNumeric: "tabular-nums",
  },
  "& .MuiTableBody-root .MuiTableRow-root:hover": {
    backgroundColor: rowHoverBg(theme),
  },
});
